/**
 * JailAnimationOverlay - Cinematic "The Jailhouse Mugshot" Animation Overlay
 *
 * Displays a transparent modal overlay featuring iron prison cell bars slamming down,
 * a burly King guard card in a police cap marching in, grabbing the player's card,
 * frantic inverse-kinematics kicking legs, convict jumpsuit & chalkboard mugshot plaque,
 * and a blinding flashbulb snapshot with realistic camera shutter audio.
 */

let activeJailOverlayInstance = null;

export class JailAnimationOverlay {
  /**
   * Displays the Jailhouse Mugshot animation in an interactive overlay.
   *
   * @param {Object} [options]
   * @param {string} [options.playerName] - Name of player jailed (e.g. "You", "Computer")
   * @param {string} [options.who] - Seat ID ('p1', 'p2', etc.)
   * @param {number} [options.jailHole] - The board hole number of the jail trap
   * @param {number} [options.score] - Current score of the jailed player
   * @param {Object} [options.card] - Card object { r, s } representing the player's card
   * @param {Object} [options.settings] - Game settings object
   * @param {Function} [options.onComplete] - Callback on dismiss or completion
   * @returns {JailAnimationOverlay|null}
   */
  static show(options = {}) {
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && (settings.jailAnimation === 'no' || settings.jailAnimation === 'off' || settings.jailAnimation === false)) {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeJailOverlayInstance) {
      activeJailOverlayInstance.close(false);
    }

    const instance = new JailAnimationOverlay(options);
    activeJailOverlayInstance = instance;
    instance.mount();
    return instance;
  }

  static hide() {
    if (activeJailOverlayInstance) {
      activeJailOverlayInstance.close(true);
    }
  }

  constructor(options = {}) {
    this.options = options;
    this.onComplete = options.onComplete || (() => {});
    this.raf = 0;
    this.running = false;
    this.overlayEl = null;
    this.autoCloseTimer = null;
    this.safetyTimer = null;
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  formatCard(card, fallbackRank = 'J', fallbackSuit = '♠') {
    if (!card) {
      return { r: fallbackRank, s: fallbackSuit, isRed: fallbackSuit === '♥' || fallbackSuit === '♦' };
    }
    if (Array.isArray(card) && card.length > 0) {
      card = card[card.length - 1];
    }
    if (typeof card === 'string') {
      const match = card.match(/^([0-9AJQK]+)([HDSC♥♦♠♣]?)$/i);
      if (match) {
        card = { r: match[1], s: match[2] };
      }
    }
    let r = card.r !== undefined ? card.r : (card.rank !== undefined ? card.rank : fallbackRank);
    if (r === 1 || r === '1') r = 'A';
    else if (r === 11 || r === '11') r = 'J';
    else if (r === 12 || r === '12') r = 'Q';
    else if (r === 13 || r === '13') r = 'K';
    r = String(r).toUpperCase();

    let s = card.s || card.suit || fallbackSuit;
    if (s === 'H' || s === 'h') s = '♥';
    else if (s === 'D' || s === 'd') s = '♦';
    else if (s === 'S' || s === 's') s = '♠';
    else if (s === 'C' || s === 'c') s = '♣';
    const isRed = s === '♥' || s === '♦';
    return { r, s, isRed };
  }

  buildHtml() {
    const prisonerName = this.options.playerName || 'Player';
    const jailHole = this.options.jailHole !== undefined ? this.options.jailHole : 25;
    const score = this.options.score !== undefined ? this.options.score : jailHole;
    const prisonerCard = this.formatCard(this.options.card, 'J', '♠');

    return `
      <div class="siren-glow"></div>
      <button class="jail-skip-btn" id="jailSkipBtn" type="button" title="Skip animation (Esc)">Skip ✕</button>

      <div class="jail-banner">
        <span class="jail-badge">🚨 BUSTED! GO TO JAIL</span>
        <h2 class="jail-title">${prisonerName} Thrown in the Clink!</h2>
        <p class="jail-subtitle">Landed on Jail Hole ${jailHole} — Must serve 3 missed turns!</p>
      </div>

      <div class="jail-stage" id="jailStage">
        <!-- Height Measurement Lines on Wall -->
        <div class="height-chart">
          <div class="height-line"><span class="height-label">6' 0"</span><span class="height-label right">6' 0"</span></div>
          <div class="height-line"><span class="height-label">5' 6"</span><span class="height-label right">5' 6"</span></div>
          <div class="height-line"><span class="height-label">5' 0"</span><span class="height-label right">5' 0"</span></div>
          <div class="height-line"><span class="height-label">4' 6"</span><span class="height-label right">4' 6"</span></div>
          <div class="height-line"><span class="height-label">4' 0"</span><span class="height-label right">4' 0"</span></div>
        </div>

        <!-- Guard Card (Burly King Police Officer) -->
        <div class="card" id="jailGuardCard">
          <div class="police-cap">
            <div class="cap-crown"></div>
            <div class="cap-visor"></div>
            <div class="cap-badge"></div>
          </div>
          <div class="face">
            <div class="corner"><span class="r">K</span><span class="s">♠</span></div>
            <div class="guard-mustache"></div>
            <div class="pip">♠</div>
            <div class="corner br"><span class="r">K</span><span class="s">♠</span></div>
          </div>
          <div class="guard-uniform">
            <div class="guard-badge">⭐</div>
          </div>
          <div class="guard-arm" id="guardArm">
            <span class="guard-hand">✊</span>
          </div>
          <div class="legs">
            <div class="leg l" id="guardLegL">
              <div class="thigh" id="guardThighL">
                <div class="shin" id="guardShinL">
                  <div class="foot"></div>
                </div>
              </div>
            </div>
            <div class="leg r" id="guardLegR">
              <div class="thigh" id="guardThighR">
                <div class="shin" id="guardShinR">
                  <div class="foot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Prisoner Card -->
        <div class="card" id="jailPrisonerCard" style="color: ${prisonerCard.isRed ? 'var(--red)' : 'var(--ink)'};">
          <div class="prisoner-sweat">💧</div>
          <div class="face">
            <div class="corner"><span class="r">${prisonerCard.r}</span><span class="s">${prisonerCard.s}</span></div>
            <div class="pip">${prisonerCard.s}</div>
            <div class="corner br"><span class="r">${prisonerCard.r}</span><span class="s">${prisonerCard.s}</span></div>
          </div>
          <div class="convict-suit" id="convictSuit">
            <div class="convict-stencil">JAIL #CC-${jailHole}</div>
          </div>
          <div class="mugshot-plaque" id="mugshotPlaque">
            <div class="plaque-header">★ COUNTY JAIL ★</div>
            <div class="plaque-name">${prisonerName}</div>
            <div class="plaque-booking">BOOKING #CC-${jailHole} • ${score} PTS</div>
            <div class="plaque-turns">SENTENCE: 3 MISSED TURNS</div>
          </div>
          <div class="legs" id="prisonerLegs">
            <div class="leg l" id="prisLegL">
              <div class="thigh" id="prisThighL">
                <div class="shin" id="prisShinL">
                  <div class="foot"></div>
                </div>
              </div>
            </div>
            <div class="leg r" id="prisLegR">
              <div class="thigh" id="prisThighR">
                <div class="shin" id="prisShinR">
                  <div class="foot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Camera Focus Reticle -->
        <div class="camera-reticle" id="cameraReticle">
          <div class="reticle-corner tl"></div>
          <div class="reticle-corner tr"></div>
          <div class="reticle-corner bl"></div>
          <div class="reticle-corner br"></div>
        </div>

        <!-- Floor -->
        <div class="jail-floor"></div>

        <!-- Iron Prison Cell Bars (Foreground) -->
        <div class="prison-bars-layer" id="prisonBarsLayer">
          <div class="bars-container">
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
            <div class="iron-bar"></div>
          </div>
          <div class="prison-beam top">
            <div class="prison-rivet"></div><div class="prison-rivet"></div><div class="prison-rivet"></div>
            <div class="prison-rivet"></div><div class="prison-rivet"></div><div class="prison-rivet"></div>
          </div>
          <div class="prison-beam bottom">
            <div class="prison-rivet"></div><div class="prison-rivet"></div><div class="prison-rivet"></div>
            <div class="prison-rivet"></div><div class="prison-rivet"></div><div class="prison-rivet"></div>
          </div>
        </div>

        <!-- Camera Flashbulb -->
        <div class="jail-flash" id="jailFlash"></div>
      </div>
    `;
  }

  mount() {
    let el = document.getElementById('jailAnimationOverlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'jailAnimationOverlay';
      el.className = 'jail-animation-overlay';
      document.body.appendChild(el);
    }
    this.overlayEl = el;
    this.overlayEl.innerHTML = this.buildHtml();

    // Attach event listeners
    const skipBtn = el.querySelector('#jailSkipBtn');
    if (skipBtn) {
      skipBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close(true);
      });
    }

    el.addEventListener('click', () => {
      this.close(true);
    });

    window.addEventListener('keydown', this.handleKeyDown);

    // Trigger reveal transition
    requestAnimationFrame(() => {
      el.classList.add('active');
      this.startSequence();
    });

    // Safety fallback timer (max 4.8 seconds)
    this.safetyTimer = setTimeout(() => {
      this.close(true);
    }, 4800);
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') {
      this.close(true);
    }
  }

  getAudio() {
    return (typeof window !== 'undefined' && window.audioManager)
      ? window.audioManager
      : (this.options.audioManager || null);
  }

  startSequence() {
    const stage = this.overlayEl.querySelector('#jailStage');
    const barsLayer = this.overlayEl.querySelector('#prisonBarsLayer');
    const guard = this.overlayEl.querySelector('#jailGuardCard');
    const prisoner = this.overlayEl.querySelector('#jailPrisonerCard');
    const reticle = this.overlayEl.querySelector('#cameraReticle');
    const flash = this.overlayEl.querySelector('#jailFlash');

    // IK Leg joints for Guard
    const guardThighL = this.overlayEl.querySelector('#guardThighL');
    const guardShinL = this.overlayEl.querySelector('#guardShinL');
    const guardThighR = this.overlayEl.querySelector('#guardThighR');
    const guardShinR = this.overlayEl.querySelector('#guardShinR');

    // IK Leg joints for Prisoner
    const prisThighL = this.overlayEl.querySelector('#prisThighL');
    const prisShinL = this.overlayEl.querySelector('#prisShinL');
    const prisThighR = this.overlayEl.querySelector('#prisThighR');
    const prisShinR = this.overlayEl.querySelector('#prisShinR');

    const sfx = this.getAudio();

    // 1. Initial State: Prison Bars Slam Down!
    setTimeout(() => {
      if (barsLayer) barsLayer.classList.add('slammed');
      if (sfx && typeof sfx.jailClang === 'function') {
        sfx.jailClang();
      } else if (sfx && typeof sfx.reactionBoom === 'function') {
        sfx.reactionBoom();
      }
    }, 120);

    let startTime = performance.now();
    this.running = true;

    // Timeline flags
    let grabbed = false;
    let flashed = false;
    let dragging = false;

    const loop = (now) => {
      if (!this.running) return;
      const elapsed = (now - startTime) / 1000; // in seconds

      const stageW = (stage && stage.clientWidth) || 800;
      const prisBaseX = stageW * 0.54;

      // PHASE 1: Guard Marches In (0.3s -> 1.1s)
      if (elapsed < 1.1) {
        const t = Math.max(0, (elapsed - 0.2) / 0.9);
        const guardX = -90 + t * (prisBaseX - 68 - (-90));
        if (guard) {
          guard.style.left = `${guardX}px`;
        }

        // Guard walking IK cycle
        const walkCycle = elapsed * 10;
        const thL = Math.sin(walkCycle) * 28;
        const thR = Math.sin(walkCycle + Math.PI) * 28;
        const shL = Math.max(0, -Math.sin(walkCycle) * 35);
        const shR = Math.max(0, -Math.sin(walkCycle + Math.PI) * 35);

        if (guardThighL) guardThighL.style.transform = `rotate(${thL}deg)`;
        if (guardShinL) guardShinL.style.transform = `rotate(${shL}deg)`;
        if (guardThighR) guardThighR.style.transform = `rotate(${thR}deg)`;
        if (guardShinR) guardShinR.style.transform = `rotate(${shR}deg)`;

        // Prisoner waiting in place
        if (prisThighL) prisThighL.style.transform = 'rotate(0deg)';
        if (prisShinL) prisShinL.style.transform = 'rotate(0deg)';
        if (prisThighR) prisThighR.style.transform = 'rotate(0deg)';
        if (prisShinR) prisShinR.style.transform = 'rotate(0deg)';
      }
      // PHASE 2: Guard Grabs Prisoner & Prisoner Frantically Kicks Legs! (1.1s -> 2.1s)
      else if (elapsed < 2.1) {
        if (guard) {
          guard.style.left = `${prisBaseX - 68}px`;
        }
        if (!grabbed) {
          grabbed = true;
          if (prisoner) {
            prisoner.classList.add('has-suit');
            prisoner.classList.add('has-plaque');
          }
          if (reticle) reticle.classList.add('active');
          if (sfx && typeof sfx.snap === 'function') sfx.snap();
        }

        // Prisoner hoisted into air & body wobbles
        const kickTime = elapsed - 1.1;
        const liftY = -26 + Math.sin(kickTime * 14) * 4;
        const bodyRot = Math.sin(kickTime * 16) * 7;
        if (prisoner) {
          prisoner.style.transform = `translateX(-50%) translateY(${liftY}px) rotate(${bodyRot}deg)`;
        }

        // FRANTIC KICKING IK LEGS! High frequency cycling and knee bends
        const kickRate = 22; // ~7.5 kicks per second
        const kickL = Math.sin(kickTime * kickRate);
        const kickR = Math.sin(kickTime * kickRate + Math.PI);

        // Dynamic thigh & shin angles for frantic flailing
        const pThL = -25 + kickL * 42;
        const pShL = 30 + Math.max(0, -kickL * 65);
        const pThR = -25 + kickR * 42;
        const pShR = 30 + Math.max(0, -kickR * 65);

        if (prisThighL) prisThighL.style.transform = `rotate(${pThL}deg)`;
        if (prisShinL) prisShinL.style.transform = `rotate(${pShL}deg)`;
        if (prisThighR) prisThighR.style.transform = `rotate(${pThR}deg)`;
        if (prisShinR) prisShinR.style.transform = `rotate(${pShR}deg)`;

        // Guard standing firm holding collar
        if (guardThighL) guardThighL.style.transform = 'rotate(-4deg)';
        if (guardShinL) guardShinL.style.transform = 'rotate(4deg)';
        if (guardThighR) guardThighR.style.transform = 'rotate(8deg)';
        if (guardShinR) guardShinR.style.transform = 'rotate(2deg)';
      }
      // PHASE 3: Camera Flash & Polaroid Snapshot Freeze (2.1s -> 2.5s)
      else if (elapsed < 2.5) {
        if (!flashed) {
          flashed = true;
          if (flash) flash.classList.add('active');
          if (reticle) reticle.classList.remove('active');
          if (sfx && typeof sfx.cameraShutter === 'function') {
            sfx.cameraShutter();
          } else if (sfx && typeof sfx.confettiPop === 'function') {
            sfx.confettiPop();
          }
          setTimeout(() => {
            if (flash) flash.classList.remove('active');
          }, 140);
        }

        // Brief freeze pose for mugshot
        if (prisoner) {
          prisoner.style.transform = `translateX(-50%) translateY(-22px) rotate(-3deg)`;
        }
        if (prisThighL) prisThighL.style.transform = 'rotate(-18deg)';
        if (prisShinL) prisShinL.style.transform = 'rotate(45deg)';
        if (prisThighR) prisThighR.style.transform = 'rotate(22deg)';
        if (prisShinR) prisShinR.style.transform = 'rotate(15deg)';
      }
      // PHASE 4: Guard Drags Frantic Prisoner into the Cell! (2.5s -> 3.3s)
      else if (elapsed < 3.3) {
        if (!dragging) dragging = true;
        const dragProgress = (elapsed - 2.5) / 0.8;
        const exitOffset = dragProgress * (stageW * 0.45);

        const guardCurX = prisBaseX - 80 + exitOffset;
        if (guard) guard.style.left = `${guardCurX}px`;

        const prisCurX = prisBaseX + exitOffset;
        const dragLift = -20 + Math.sin(elapsed * 12) * 5;
        const dragRot = 12 + Math.sin(elapsed * 14) * 6;
        if (prisoner) {
          prisoner.style.left = `${prisCurX}px`;
          prisoner.style.transform = `translateX(-50%) translateY(${dragLift}px) rotate(${dragRot}deg)`;
        }

        // Continued frantic kicking and dragging feet
        const dragKickRate = 18;
        const dkL = Math.sin(elapsed * dragKickRate);
        const dkR = Math.sin(elapsed * dragKickRate + Math.PI);

        if (prisThighL) prisThighL.style.transform = `rotate(${-15 + dkL * 35}deg)`;
        if (prisShinL) prisShinL.style.transform = `rotate(${25 + Math.max(0, -dkL * 55)}deg)`;
        if (prisThighR) prisThighR.style.transform = `rotate(${-15 + dkR * 35}deg)`;
        if (prisShinR) prisShinR.style.transform = `rotate(${25 + Math.max(0, -dkR * 55)}deg)`;

        // Guard walking dragging
        const gWalk = elapsed * 8;
        if (guardThighL) guardThighL.style.transform = `rotate(${Math.sin(gWalk) * 22}deg)`;
        if (guardShinL) guardShinL.style.transform = `rotate(${Math.max(0, -Math.sin(gWalk) * 28)}deg)`;
        if (guardThighR) guardThighR.style.transform = `rotate(${Math.sin(gWalk + Math.PI) * 22}deg)`;
        if (guardShinR) guardShinR.style.transform = `rotate(${Math.max(0, -Math.sin(gWalk + Math.PI) * 28)}deg)`;
      }
      // PHASE 5: Finished
      else {
        this.close(true);
        return;
      }

      this.raf = requestAnimationFrame(loop);
    };

    this.raf = requestAnimationFrame(loop);
  }

  close(triggerCallback = true) {
    if (this.safetyTimer) {
      clearTimeout(this.safetyTimer);
      this.safetyTimer = null;
    }
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    this.running = false;

    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.overlayEl) {
      this.overlayEl.classList.remove('active');
      setTimeout(() => {
        if (this.overlayEl && this.overlayEl.parentNode) {
          this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
        this.overlayEl = null;
      }, 300);
    }

    if (activeJailOverlayInstance === this) {
      activeJailOverlayInstance = null;
    }

    if (triggerCallback && typeof this.onComplete === 'function') {
      const cb = this.onComplete;
      this.onComplete = null;
      cb();
    }
  }
}
